import * as Popover from '@radix-ui/react-popover';
import type { AutomationCategoryResponse } from '@reach/shared-types/automation-category';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { CategoryChecklist } from '../CategoryChecklist/CategoryChecklist';
import { CategoryEditDialog } from '../CategoryEditDialog/CategoryEditDialog';
import { keepOpenWhileEditingCategory } from '../CategoryEditDialog/categoryEditDialogTarget';
import { CategoryPill } from '../CategoryPill/CategoryPill';
import { useAutomationCategories } from '../queries';
import styles from './TemplateCategoryPicker.module.css';

interface TemplateCategoryPickerProps {
  selectedCategoryIds: string[];
  onSelectedCategoryIdsChange: (categoryIds: string[]) => void;
}

/**
 * The selected categories as removable pills, plus an "Add categories" menu to assign, create,
 * and edit them. Used in the automation settings dialog.
 */
export function TemplateCategoryPicker({
  selectedCategoryIds,
  onSelectedCategoryIdsChange,
}: TemplateCategoryPickerProps) {
  const [categoryBeingEdited, setCategoryBeingEdited] = useState<AutomationCategoryResponse | null>(
    null,
  );
  const { data: categories = [] } = useAutomationCategories();

  const selectedCategories = selectedCategoryIds
    .map(categoryId => categories.find(category => category.id === categoryId))
    .filter((category): category is AutomationCategoryResponse => category !== undefined);

  const removeCategory = (categoryId: string) =>
    onSelectedCategoryIdsChange(selectedCategoryIds.filter(selectedId => selectedId !== categoryId));

  return (
    <div className={styles.picker}>
      <ul className={styles.selected} aria-label="Selected categories">
        {selectedCategories.map(category => (
          <li key={category.id}>
            <CategoryPill category={category} />
            <button
              type="button"
              aria-label={`Remove ${category.name}`}
              onClick={() => removeCategory(category.id)}>
              <X aria-hidden />
            </button>
          </li>
        ))}
        <li>
          <Popover.Root>
            <Popover.Trigger asChild>
              <button type="button" className={styles.addButton}>
                <Plus aria-hidden />
                Add categories
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className={styles.menu}
                align="start"
                sideOffset={6}
                onInteractOutside={keepOpenWhileEditingCategory}>
                <CategoryChecklist
                  selectedCategoryIds={selectedCategoryIds}
                  onSelectedCategoryIdsChange={onSelectedCategoryIdsChange}
                  onEditCategory={setCategoryBeingEdited}
                />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </li>
      </ul>
      {categoryBeingEdited && (
        <CategoryEditDialog
          category={categoryBeingEdited}
          onDeleted={() => removeCategory(categoryBeingEdited.id)}
          onClose={() => setCategoryBeingEdited(null)}
        />
      )}
    </div>
  );
}
